// RelationshipTab.tsx
import React, { useState } from "react";
import { Heart } from "lucide-react";
import AstriaMexicoShell from "../AstriaMexicoShell";
import { useMexicoColors } from "../AstriaMexicoThemeContext";
import {
  AstriaMexicoResultCard,
  AstriaMexicoFieldBlock,
  MXLoadingState,
  MXErrorState,
  MXChatCta,
} from "../AstriaMexicoCard";
import {
  MXPersonFields,
  MXSubmitButton,
  PersonDetails,
  EMPTY_PERSON_DETAILS,
} from "../AstriaMexicoWizard";
import { useAstriaMexicoTab } from "../useAstriaMexicoTab";
import { AstriaMexicoRelationshipData, Message } from "../../../../contexts/ChatContext";

const SUBCATEGORY_NAME = "Relationship";

const buildMessage = (self: PersonDetails, partner: PersonDetails) => `
Lee mi Relación con ${partner.name.trim() || "mi pareja"}.
Mi fecha de nacimiento: ${self.dob.trim()}.
Mi hora de nacimiento: ${self.time.trim() || "No proporcionada"}.
Mi ciudad de nacimiento: ${self.place.trim() || "No proporcionada"}.
Fecha de nacimiento de ${partner.name.trim() || "mi pareja"}: ${partner.dob.trim()}.
Hora de nacimiento de ${partner.name.trim() || "mi pareja"}: ${partner.time.trim() || "No proporcionada"}.
Ciudad de nacimiento de ${partner.name.trim() || "mi pareja"}: ${partner.place.trim() || "No proporcionada"}.
Por favor dame una lectura honesta y cálida de cómo me relaciono con esta persona.
`.trim();

// Colors must be read INSIDE AstriaMexicoShell's subtree — these small
// components are rendered as `children`, not evaluated inline, so their hook
// calls resolve after the shell's theme provider has mounted.

const ResultView: React.FC<{
  data?: AstriaMexicoRelationshipData;
  message: Message | null;
  reset: () => void;
  goToChat: () => Promise<void>;
}> = ({ data, message, reset, goToChat }) => {
  const { colors } = useMexicoColors();

  if (data) {
    return (
      <>
        <AstriaMexicoResultCard
          icon={<Heart className="w-5 h-5" />}
          title="Relación"
          subtitle="Una lectura honesta"
          shareText={`${data.opening}\n\n${data.current_vibe}\n\n${data.closing}`}
        >
          <AstriaMexicoFieldBlock label="Apertura" value={data.opening} />
          <AstriaMexicoFieldBlock label="Cómo Se Siente Ahora" value={data.current_vibe} />
          <AstriaMexicoFieldBlock label="Fortalezas" value={data.strengths} />
          <AstriaMexicoFieldBlock label="Un Ajuste Gentil" value={data.gentle_adjustment} />
          <AstriaMexicoFieldBlock label="Acción Para Hoy" value={data.today_action} />
          <AstriaMexicoFieldBlock label="Cierre" value={data.closing} />
        </AstriaMexicoResultCard>
        <button
          onClick={reset}
          className="text-xs font-semibold transition-colors"
          style={{ marginTop: 14, color: colors.mutedColor }}
        >
          Empezar Una Nueva Lectura
        </button>
        <MXChatCta onClick={goToChat} />
      </>
    );
  }

  if (message) {
    return (
      <AstriaMexicoResultCard icon={<Heart className="w-5 h-5" />} title="Relación">
        <p
          style={{
            fontSize: 15,
            lineHeight: 1.75,
            color: colors.bodyColor,
            whiteSpace: "pre-wrap",
          }}
        >
          {message.content}
        </p>
      </AstriaMexicoResultCard>
    );
  }

  return null;
};

const IntroForm: React.FC<{
  self: PersonDetails;
  setSelf: (v: PersonDetails) => void;
  partner: PersonDetails;
  setPartner: (v: PersonDetails) => void;
  onSubmit: () => void;
  formValid: boolean;
}> = ({ self, setSelf, partner, setPartner, onSubmit, formValid }) => {
  const { colors } = useMexicoColors();

  return (
    <>
      <div style={{ marginBottom: 20 }}>
        <h2
          style={{
            fontSize: 21,
            fontWeight: 700,
            color: colors.titleColor,
            marginBottom: 6,
          }}
        >
          Relación
        </h2>
        <p style={{ fontSize: 13.5, color: colors.mutedColor, lineHeight: 1.6 }}>
          Una lectura honesta y cálida sobre cómo te relacionas con alguien específico en tu vida.
        </p>
      </div>

      <MXPersonFields
        heading="Tus Datos"
        values={self}
        onChange={setSelf}
        namePlaceholder="Tu nombre"
      />
      <MXPersonFields heading="Sus Datos" values={partner} onChange={setPartner} />

      <MXSubmitButton onClick={onSubmit} disabled={!formValid} label="Revelar Nuestra Relación" />
    </>
  );
};

const RelationshipTab: React.FC = () => {
  const { loading, error, message, send, reset, goToChat } = useAstriaMexicoTab({
    subCategoryName: SUBCATEGORY_NAME,
  });

  const [self, setSelf] = useState<PersonDetails>(EMPTY_PERSON_DETAILS);
  const [partner, setPartner] = useState<PersonDetails>(EMPTY_PERSON_DETAILS);

  const started = !!message || loading || !!error;
  const formValid = !!self.dob.trim() && !!partner.dob.trim();

  const handleSubmit = () => send(buildMessage(self, partner));

  const data = message?.astriaMexicoData as AstriaMexicoRelationshipData | undefined;

  if (started) {
    return (
      <AstriaMexicoShell active="relationship">
        {loading && <MXLoadingState label="Viendo cómo se sienten juntos…" />}
        {!loading && error && <MXErrorState message={error} onRetry={handleSubmit} />}
        {!loading && !error && (
          <ResultView data={data} message={message} reset={reset} goToChat={goToChat} />
        )}
      </AstriaMexicoShell>
    );
  }

  return (
    <AstriaMexicoShell active="relationship">
      <IntroForm
        self={self}
        setSelf={setSelf}
        partner={partner}
        setPartner={setPartner}
        onSubmit={handleSubmit}
        formValid={formValid}
      />
    </AstriaMexicoShell>
  );
};

export default RelationshipTab;
